import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  Activity, Eye, EyeOff, Mail, Lock, AlertCircle, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { signInWithGoogle, signInWithMicrosoft, isFirebaseConfigured } from '@/lib/firebase';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, ssoLogin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(null); // 'google' | 'microsoft' | null
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
  });
  const [errors, setErrors] = useState({});

  // Show welcome message if redirected from trial signup
  useEffect(() => {
    if (searchParams.get('welcome') === '1') {
      toast.success('Clinic created! Sign in with your new account.', { duration: 5000 });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    const result = await login(formData.email, formData.password);
    setLoading(false);
    
    if (result.success) {
      toast.success(`Welcome back, ${result.user.name}!`);
      navigate(result.redirectTo);
    } else {
      toast.error(result.error || 'Login failed');
    }
  };
  const handleSsoLogin = async (provider) => {
    setSsoLoading(provider);
    try {
      const firebaseResult = provider === 'google'
        ? await signInWithGoogle()
        : await signInWithMicrosoft();

      const result = await ssoLogin(firebaseResult.idToken, firebaseResult.provider, {
        name: firebaseResult.name,
        avatar: firebaseResult.avatar,
      });

      if (result.success) {
        toast.success('Welcome, ' + result.user.name + '!');
        navigate(result.redirectTo);
      } else if (result.newUser) {
        // New SSO user — redirect to trial signup with pre-filled profile
        toast.info('No account found — let\'s set up your clinic!');
        navigate(result.redirectTo);
      } else {
        toast.error(result.error || 'SSO login failed');
      }
    } catch (err) {
      const msg = err?.code === 'auth/popup-closed-by-user' ? 'Sign-in cancelled' :
                  err?.code === 'auth/account-exists-with-different-credential' ? 'An account with this email already exists with a different provider' :
                  err?.message || 'SSO sign-in failed';
      toast.error(msg);
    } finally {
      setSsoLoading(null);
    }
  };
  return (
    <div className="min-h-screen flex flex-col">
      {/* Deep Navy branded banner with wave */}
      <div className="w-full bg-primary relative overflow-hidden border-b border-primary/20">
        <div className="py-10 relative z-10">
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex flex-col items-center gap-2"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur rounded-xl">
                <Activity className="h-7 w-7 text-white" />
              </div>
              <div className="text-left">
                <h1 className="text-3xl font-brand font-bold text-white tracking-tight">
                  Vivalyn<span className="font-normal text-clinical-soft">EMR</span>
                </h1>
                <p className="text-primary-100/80 text-sm">AI Systems for Life & HealthCare</p>
              </div>
            </div>
          </motion.div>
        </div>
        {/* Flowing wave SVG divider */}
        <svg className="absolute bottom-0 left-0 w-full" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ height: '30px' }}>
          <path d="M0,40 C360,70 720,0 1080,40 C1260,55 1380,20 1440,30 L1440,60 L0,60 Z" fill="hsl(210, 25%, 97%)" />
        </svg>
      </div>

      {/* Login form area */}
      <div className="flex-1 flex items-center justify-center bg-background p-4">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-foreground">Sign in to your account</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials to continue</p>
          </div>

          <Card className="border-border/80 shadow-lg">
            <CardContent className="pt-6 space-y-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@hospital.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`pl-10 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 rounded border-gray-300" />
                    <span>Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="text-sm text-accent hover:underline">
                    Forgot password?
                  </Link>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* SSO Divider & Buttons */}
              {isFirebaseConfigured && (
                <div className="space-y-3">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                    <div className="relative flex justify-center text-xs uppercase"><span className="bg-background px-2 text-muted-foreground">Or continue with</span></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10"
                      disabled={!!ssoLoading || loading}
                      onClick={() => handleSsoLogin('google')}
                    >
                      {ssoLoading === 'google' ? (
                        <span className="animate-spin mr-2">⏳</span>
                      ) : (
                        <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      )}
                      Google
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10"
                      disabled={!!ssoLoading || loading}
                      onClick={() => handleSsoLogin('microsoft')}
                    >
                      {ssoLoading === 'microsoft' ? (
                        <span className="animate-spin mr-2">⏳</span>
                      ) : (
                        <svg className="h-4 w-4 mr-2" viewBox="0 0 23 23">
                          <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                          <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                          <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                          <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
                        </svg>
                      )}
                      Microsoft
                    </Button>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>

          <p className="text-center text-xs text-muted-foreground mt-8">
            © {new Date().getFullYear()} Vivalyn Labs · AI for Doctors. Care for Patients. · All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
